import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Reveal, Item, MaskLines } from "@/components/Reveal";
import Magnetic from "@/components/Magnetic";
import { ArrowRight } from "lucide-react";

const sections = [
  {
    n: "01",
    h: "The call",
    t: "Fifteen minutes for you to show us the work that is slow, repetitive or difficult to track. You do not need a technical brief. We will ask plain questions and tell you honestly whether we can help.",
  },
  {
    n: "02",
    h: "The audit",
    t: "We follow the real work from start to finish: who receives it, what they check, where they type it again, what goes wrong and who must approve it. A focused job may take one working session; a larger ERP or CRM gets a wider agreed scope.",
  },
  {
    n: "03",
    h: "The proposal",
    t: "You get a clear recommendation: what we would change first, what we would leave alone, what your team will see, how long it should take and what it will cost. No technical theatre and no surprise extras.",
  },
  {
    n: "04",
    h: "The build",
    t: "You see working software throughout the build, not only at the end. We begin with the smallest useful version, test it with the people who do the work and keep important decisions behind human approval.",
  },
  {
    n: "05",
    h: "In use",
    t: "We launch carefully, watch what happens and fix the awkward edges. Then you choose whether to stop, improve the first workflow, or grow it into more automation, custom software, an ERP, CRM or Company Brain.",
  },
];

const blocks = [
  {
    label: "On top, or instead",
    body: "We can work with the software you already use, replace one painful part, or build your own ERP or CRM in stages. We will not ask you to throw away something that works just to make our job easier.",
    testid: "block-ontop",
  },
  {
    label: "Why we work this way",
    body: "We take on a small number of clients at a time. The people who understand your business stay involved while the system is being built, so you are not passed from a salesperson to a delivery team that has never met you.",
    testid: "block-why",
  },
  {
    label: "What it costs",
    body: "A focused first build has a clear fixed scope. Larger ERP or CRM work is broken into sensible phases. You see the project cost, any running costs and optional support before work starts, not after you are committed.",
    testid: "block-cost",
  },
];

export default function HowWeWork() {
  return (
    <div data-testid="page-how-we-work">
      <Seo
        title="How we work · AUT labs"
        description="How AUT labs discovers, architects, builds and deploys custom ERP and CRM systems, AI agents, Company Brains and AI-native operating systems."
      />

      {/* Intro */}
      <section className="border-b border-ink col-rules">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
          <Reveal className="grid grid-cols-1 md:grid-cols-12">
            <div className="md:col-span-8 lg:col-span-7">
              <Item as="p" className="eyebrow mb-6">How we work</Item>
              <h1 className="display text-[40px] md:text-[72px] lg:text-[88px] leading-[0.96]">
                <MaskLines lines={["How long,", "and how disruptive?"]} />
              </h1>
              <Item as="p" className="mt-8 max-w-xl text-lg text-ink/80">
                Two fair questions. We answer both before you commit. The aim is to make
                work easier without turning the business upside down to get there.
              </Item>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Numbered sections */}
      <section data-testid="how-sections">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          {sections.map((s, i) => (
            <Reveal
              key={s.n}
              className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-10 md:py-16 border-b border-ink items-start"
            >
              <Item className="md:col-span-3">
                <span data-testid={`how-section-${i}`} className="numeral text-[72px] md:text-[110px]">{s.n}</span>
              </Item>
              <Item className="md:col-span-4">
                <h2 className="display text-3xl md:text-5xl">{s.h}</h2>
              </Item>
              <Item className="md:col-span-5 md:pt-3">
                <p className="text-lg text-ink/85">{s.t}</p>
              </Item>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Explainer blocks */}
      <section className="border-b border-ink" data-testid="how-blocks">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-14 md:py-20 space-y-6 md:space-y-8">
          {blocks.map((b) => (
            <Reveal key={b.testid}>
              <Item className="border border-ink p-8 md:p-14 grid grid-cols-1 md:grid-cols-12 gap-6" data-testid={b.testid}>
                <p className="eyebrow md:col-span-3">{b.label}</p>
                <p className="md:col-span-9 display text-[24px] md:text-[38px] leading-[1.12]">{b.body}</p>
              </Item>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section data-testid="how-cta">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
          <Magnetic>
            <Link to="/book" className="pill pill-cobalt" data-testid="how-book-btn">
              Book a 15-minute call <ArrowRight size={16} />
            </Link>
          </Magnetic>
        </div>
      </section>
    </div>
  );
}
